import { useAuth } from '../hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router';

export function DashboardPage() {
  const { user } = useAuth();

  const { data: activeServicesData } = useQuery({
    queryKey: ['active-services'],
    queryFn: () => api.get('/api/services/list_active').then((r) => r.data),
  });

  const { data: usageData } = useQuery({
    queryKey: ['monthly-usage'],
    queryFn: () => api.get('/api/usage/list_month_usage').then((r) => r.data),
  });

  const { data: subscriptionData } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => api.get('/api/subscription/get').then((r) => r.data),
  });

  // Active services count — API returns array for list_active
  const activeServiceCount = Array.isArray(activeServicesData)
    ? activeServicesData.length
    : activeServicesData?.query?.active_service_count ?? 0;

  // Monthly usage — get latest month's transaction count
  const monthlyUsages = usageData?.query?.data ?? (Array.isArray(usageData) ? usageData : []);
  const currentMonthUsage = monthlyUsages[monthlyUsages.length - 1];

  // Credit balance
  const creditBalance = subscriptionData?.queryBalance?.credit_balance ?? subscriptionData?.queryBalance?.credit_balance ?? 0;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Hello {user?.firstname},</h1>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
        {/* Overview card — 8 cols */}
        <div className="md:col-span-8">
          <Card>
            <h3 className="card-header text-lg font-semibold px-6 py-4 border-b">
              Overview
            </h3>
            <CardContent className="pt-3 px-6">
              <ul className="space-y-0">
                <li className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span>Welcome to our portal! Start by creating an integration service from our catalog</span>
                  <Link to="/services/catalog" className="text-[#cc0404] hover:underline ml-2 whitespace-nowrap">View our services catalog.</Link>
                </li>
                <li className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span>You have {activeServiceCount} active services.</span>
                  <Link to="/services" className="text-[#cc0404] hover:underline ml-2 whitespace-nowrap">View active services.</Link>
                </li>
                <li className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span>{currentMonthUsage?.count_transactions ?? currentMonthUsage?.count ?? 0} service requests executed this month.</span>
                  <Link to="/usage" className="text-[#cc0404] hover:underline ml-2 whitespace-nowrap">View usage details.</Link>
                </li>
                <li className="flex items-center justify-between py-3">
                  <span>You have {creditBalance} remaining credits.</span>
                  <Link to="/subscription" className="text-[#cc0404] hover:underline ml-2 whitespace-nowrap">View your balance.</Link>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Getting Started card — 4 cols */}
        <div className="md:col-span-4">
          <Card>
            <h3 className="card-header text-lg font-semibold px-6 py-4 border-b">
              Getting Started
            </h3>
            <CardContent className="pt-3 px-6">
              <ul className="space-y-0">
                <li className="py-3 border-b border-gray-100">
                  <span className="text-[#cc0404]">Getting Started</span>
                </li>
                <li className="py-3 border-b border-gray-100">
                  <span className="text-[#cc0404]">User guide</span>
                </li>
                <li className="py-3 border-b border-gray-100">
                  <span className="text-[#cc0404]">FAQ</span>
                </li>
                <li className="py-3">
                  <Link to="/help/contact" className="text-[#cc0404] hover:underline">Contact Us</Link>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Find out more card */}
      <Card>
        <h3 className="card-header text-lg font-semibold px-6 py-4 border-b">
          Find out more
        </h3>
        <CardContent className="pt-3 px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex gap-3">
              <div className="flex-shrink-0">
                <svg className="w-10 h-10 text-[#767676]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55C7.79 13 6 14.79 6 17s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                </svg>
              </div>
              <div>
                <h5 className="font-medium">link</h5>
                <p className="text-sm text-gray-500">description</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-shrink-0">
                <svg className="w-10 h-10 text-[#767676]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
                </svg>
              </div>
              <div>
                <h5 className="font-medium">
                  <a href="https://www.youtube.com/@Connectfy_net" target="_blank" rel="noopener noreferrer" className="text-[#cc0404] hover:underline">Connectfy Channel</a>
                </h5>
                <p className="text-sm text-gray-500">Visit our YouTube channel for more information.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-shrink-0">
                <svg className="w-10 h-10 text-[#767676]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7h3v1.5zM9 9.5h1v-1H9v1zM14 9.5h1v-1h-1v1z"/>
                </svg>
              </div>
              <div>
                <h5 className="font-medium">link</h5>
                <p className="text-sm text-gray-500">description</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}